import { useState, useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Alert, AlertDescription } from "./ui/alert";
import { TrendingUp, Lock, Mail, User, Building2, Eye, EyeOff, ArrowLeft, CheckCircle2, AlertCircle, Info, Send } from "lucide-react";
import { toast } from "sonner";
import { FinSightLogo } from "./FinSightLogo";
import { authService } from '../services/authService';
import { mfaService } from '../services/mfaService';
import enterpriseService from '../services/enterpriseService';
import { MfaVerificationDialog } from './MfaVerificationDialog';
import { Footer } from "./Footer";
//import { googleAuthService } from '../services/googleAuthService';
import { useGoogleLogin } from '@react-oauth/google';

export function LoginPage({ onLogin }) {
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [signupFirstName, setSignupFirstName] = useState("");
  const [signupLastName, setSignupLastName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupConfirmPassword, setSignupConfirmPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    ['onboardingComplete', 'onboardingCompleted'].forEach(k => localStorage.removeItem(k));
  }, []);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [signupPlan, setSignupPlan] = useState("INDIVIDUAL");
  const [companySize, setCompanySize] = useState("");
  const [orgExistsForDomain, setOrgExistsForDomain] = useState(null);
  const [orgCheckLoading, setOrgCheckLoading] = useState(false);
  const [isRequestingAccess, setIsRequestingAccess] = useState(false);
  const domainCheckRef = useRef(null);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordSubmitted, setForgotPasswordSubmitted] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState("");
  const [loginError, setLoginError] = useState("");
  const [signupError, setSignupError] = useState("");
  const [showMfaDialog, setShowMfaDialog] = useState(false);
  const [mfaType, setMfaType] = useState('LOGIN');
  const [invitation, setInvitation] = useState(null);
  const [activeTab, setActiveTab] = useState("login");
  const [signupSuccessMessage, setSignupSuccessMessage] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const invitationId = params.get('invitation');
    const tab = params.get('tab');
    if (tab === 'signup') setActiveTab('signup');
    if (invitationId) {
      enterpriseService.getInvitation(invitationId)
        .then(data => {
          setInvitation(data);
          setSignupEmail(data.invitedEmail || '');
          setSignupPlan('INDIVIDUAL');
          setActiveTab('signup');
        })
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (domainCheckRef.current) clearTimeout(domainCheckRef.current);

    if (signupPlan !== 'ENTERPRISE' || !signupEmail || !signupEmail.includes('@') || invitation) {
      setOrgExistsForDomain(null);
      return;
    }

    const domain = signupEmail.split('@')[1];
    setOrgCheckLoading(true);

    domainCheckRef.current = setTimeout(async () => {
      try {
        const data = await enterpriseService.checkDomain(domain);
        setOrgExistsForDomain(data.exists ? data : null);
      } catch {
        setOrgExistsForDomain(null);
      } finally {
        setOrgCheckLoading(false);
      }
    }, 500);

    return () => {
      if (domainCheckRef.current) clearTimeout(domainCheckRef.current);
    };
  }, [signupEmail, signupPlan, invitation]);

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setForgotPasswordError("");

    if (!forgotPasswordEmail) {
      setForgotPasswordError("Please enter your email address");
      return;
    }

    if (!forgotPasswordEmail.includes("@")) {
      setForgotPasswordError("Please enter a valid email address");
      return;
    }

    setForgotPasswordLoading(true);

    try {
      await authService.requestPasswordReset(forgotPasswordEmail);
      setForgotPasswordSubmitted(true);
      toast.success("Password reset email sent!");
    } catch (err) {
      setForgotPasswordError(err.message || "Failed to send reset email. Please try again.");
      toast.error("Failed to send reset email");
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleCloseForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotPasswordEmail("");
    setForgotPasswordSubmitted(false);
    setForgotPasswordError("");
  };

const handleGoogleLogin = useGoogleLogin({
    flow: 'auth-code',
    onSuccess: async (codeResponse) => {
        console.log(codeResponse);
        const googleAuthData = {
          code: codeResponse.code,
          redirectUri: window.location.origin
        };
        setIsLoading(true);
        setLoginError("");
        try {
          const response = await authService.loginWithGoogleAuthCode(googleAuthData);
          toast.success("Welcome back!");
          onLogin(response.email || loginEmail, false);
        } catch (error) {
          console.error('Google login error:', error);
          setLoginError("Google login failed. Please try again.");
          toast.error("Google login failed");
        } finally {
          setIsLoading(false);
        }
    },
    onError: errorResponse => {
      console.log(errorResponse);
      setLoginError("Google login failed");
    },
});


  const handleGoogleSignup = useGoogleLogin({
    onSuccess: async tokenResponse => {
      setSignupError("");
      try {
        console.log(tokenResponse);
        // Get user info from Google
        const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
          headers: {
            Authorization: `Bearer ${tokenResponse.access_token}`,
          },
        });
        console.log('userInfoResponse', userInfoResponse);
        const userInfo = await userInfoResponse.json();
        console.log('userInfo', userInfo);
        // Call your backend with the correct user data structure
        setIsLoading(true);
        await authService.register({
          firstName: userInfo.given_name, 
          lastName: userInfo.family_name, 
          name: userInfo.name, 
          email: userInfo.email, 
          password: 'nopassword', 
          provider: 'google',
          agreeServiceTermPrivacyPolicy: true
        });
        
        toast.success("Account created successfully with Google!");
        showSignupSuccess();
      } catch (error) {
        console.error('Google signup error:', error);
        setSignupError("Google signup failed. Please try again.");
        toast.error("Google login failed");
      } finally {
        setIsLoading(false);
      }
    },
    onError: (error) => {
      setSignupError("Google login failed");
      toast.error('Google login failed');
    }
  });
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setSignupSuccessMessage("");
    
    if (!loginEmail || !loginPassword) {
      setLoginError("Please fill in all fields");
      return;
    }

    if (!loginEmail.includes("@")) {
      setLoginError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    try {
      const response = await authService.loginUser({
        email: loginEmail, 
        password: loginPassword, 
        provider: 'local'
      });

      console.log('[Login] Response:', response);

      if (response.requireMfa) {
        console.log('[Login] MFA required - showing dialog');
        setIsLoading(false);
        setMfaType('LOGIN');
        setShowMfaDialog(true);
        return;
      }

      if (response.membershipStatus === 'ACTIVE') {
        toast.success("Welcome back!");
        onLogin(loginEmail, false);
        return;
      }

      if (response.orgAccessRequired) {
        onLogin(loginEmail, false);
        return;
      }
      
      toast.success("Welcome back!");
      onLogin(loginEmail, false);
    } catch (error) {
        console.error('Login error:', error);
        setLoginError(error.message || "Login failed. Please check your credentials.");
        toast.error("Login failed");
    }
    setIsLoading(false);
  };

  const clearStaleSession = () => {
    ['onboardingComplete', 'onboardingCompleted', 'userData', 'authToken', 'isAuthenticated', 'userId', 'userRole', 'orgAccessRequired', 'orgId', 'orgName', 'membershipStatus'].forEach(k => localStorage.removeItem(k));
  };

  const showSignupSuccess = () => {
    // Ensure the just-created account is not auto-authenticated
    ['authToken', 'isAuthenticated', 'userId', 'userEmail', 'userRole', 'fullName', 'orgAccessRequired', 'orgId', 'orgName', 'membershipStatus', 'orgOwnerEmail', 'subscriptionStatus'].forEach(k => localStorage.removeItem(k));
    setSignupSuccessMessage("Thanks for Sign-up to WealthBrainAI platform. Please Login to proceed");
    setSignupError("");
    setActiveTab("login");
  };

  const handleRequestAccess = async () => {
    if (isRequestingAccess) return;
    setIsRequestingAccess(true);
    try {
      await enterpriseService.requestAccess();
      localStorage.setItem('plan', 'INDIVIDUAL');
      toast.success("Your request to join has been sent to the organization admin for approval.");
      showSignupSuccess();
    } catch (error) {
      toast.error(error.message || "Failed to send request. Please contact your admin.");
    } finally {
      setIsRequestingAccess(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setSignupError("");
    clearStaleSession();

    if (!signupFirstName || !signupLastName || !signupEmail || !signupPassword || !signupConfirmPassword) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (signupPlan !== 'INDIVIDUAL' && !orgExistsForDomain && !companySize) {
      toast.error("Please select your company size");
      return;
    }

    if (!signupEmail.includes("@")) {
      setSignupError("Please enter a valid email address");
      return;
    }

    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;
    if (!passwordRegex.test(signupPassword)) {
      setSignupError("Password must be at least 8 characters long and contain at least one letter and one number");
      return;
    }

    if (signupPassword !== signupConfirmPassword) {
      setSignupError("Passwords do not match");
      return;
    }

    if (!termsAccepted) {
      setSignupError("Please agree to the Terms of Service and Privacy Policy");
      return;
    }

    setIsLoading(true);
    try {
      const fullName = `${signupFirstName} ${signupLastName}`;
      const response = await authService.register({
        firstName: signupFirstName, 
        lastName: signupLastName, 
        name: fullName, 
        email: signupEmail, 
        password: signupPassword,
        agreeServiceTermPrivacyPolicy: termsAccepted,
        plan: signupPlan,
        companySize
      });

      if (response.requireMfa) {
        if (response.userId) localStorage.setItem('userId', response.userId);
        if (response.role) localStorage.setItem('userRole', response.role);
        if (response.fullName) localStorage.setItem('fullName', response.fullName);
        setMfaType('SIGNUP');
        setShowMfaDialog(true);
        return;
      }

      if (orgExistsForDomain) {
        await handleRequestAccess();
        return;
      }

      if (response.membershipStatus === 'ACTIVE') {
        toast.success("Account created successfully!");
        showSignupSuccess();
        return;
      }

      if (response.orgAccessRequired) {
        toast.success("Account created! Please request access to join your organization.");
        showSignupSuccess();
        return;
      }
      
      toast.success("Account created successfully!");
      showSignupSuccess();
    } catch (error) {
      console.error('Registration error:', error);
      setSignupError(error.message || "Account creation failed. Please try again.");
      toast.error(error.message || "Account creation failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaVerified = async (code) => {
    setShowMfaDialog(false);
    setIsLoading(true);
    try {
      if (mfaType === 'LOGIN') {
        await authService.loginWithMfa(loginEmail, code);
        toast.success("Welcome back!");
        onLogin(loginEmail, false);
      } else {
        await mfaService.verifyCode(signupEmail, code, 'SIGNUP');
        toast.success("Account created successfully!");
        showSignupSuccess();
      }
    } catch (err) {
      setLoginError(err.message || 'Verification failed');
      toast.error('Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaCancel = () => {
    setShowMfaDialog(false);
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-6xl grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
        <div className="space-y-6">
          <div className="space-y-4">
            <FinSightLogo variant="icon" size="xl" />
            <h1 className="text-4xl">
              {/* Financial Insights Hub */}
              Wealth Brain AI Hub

            </h1>
            <p className="text-xl text-muted-foreground max-w-md">
              Connect with investors, analysts, and advisors. Collaborate on financial
              insights and grow your portfolio.
            </p>
          </div>

          <div className="space-y-4 pt-8">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="h-5 w-5" />
              </div>
              <div>
                <h3 className="mb-1">Real-time Portfolio Tracking</h3>
                <p className="text-sm text-muted-foreground">
                  Monitor your investments with comprehensive analytics and insights
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center flex-shrink-0">
                <User className="h-5 w-5" />
              </div>
              <div>
                <h3 className="mb-1">AI-Powered Research and Recommendations</h3>
                <div className="text-sm text-muted-foreground">
                  <ul className="list-disc list-inside space-y-1">
                    <li>Enable AI-powered research capabilities</li>
                    <li>Get matched with professionals based on expertise and goals</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0">
                <Lock className="h-5 w-5" />
              </div>
              <div>
                <h3 className="mb-1">Secure Collaboration</h3>
                <p className="text-sm text-muted-foreground">
                  Share insights and communicate safely with your network
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Login/Signup Form */}
        {/* <div className="flex justify-end"> */}
          <Card className="w-full shadow-xl">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl">Welcome</CardTitle>
              <CardDescription>
                Sign in to your account or create a new one
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs 
                value={activeTab}
                onValueChange={(v) => {
                  setActiveTab(v);
                  setLoginError("");
                  setSignupError("");
                  setSignupSuccessMessage("");
                }}
                className="w-full"
              >
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login">Login</TabsTrigger>
                  <TabsTrigger value="signup">Sign Up</TabsTrigger>
                </TabsList>

                {/* Login Tab */}
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    {signupSuccessMessage && (
                      <Alert className="bg-green-50 border-green-200">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <AlertDescription className="text-green-700">{signupSuccessMessage}</AlertDescription>
                      </Alert>
                    )}
                    {loginError && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{loginError}</AlertDescription>
                      </Alert>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="john@example.com"
                          value={loginEmail}
                          onChange={(e) => setLoginEmail(e.target.value)}
                          className="pl-10"
                          disabled={isLoading}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="login-password"
                          type={showLoginPassword ? "text" : "password"}
                          placeholder="Enter your password"
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          className="pl-10 pr-10"
                          disabled={isLoading}
                        />
                        <button
                          type="button"
                          onClick={() => setShowLoginPassword(!showLoginPassword)}
                          className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                        >
                          {showLoginPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="remember"
                          checked={rememberMe}
                          onCheckedChange={(checked) => setRememberMe(checked)}
                        />
                        <Label
                          htmlFor="remember"
                          className="text-sm cursor-pointer"
                        >
                          Remember me
                        </Label>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-sm text-primary hover:underline"
                      >
                        Forgot password?
                      </button>
                    </div>

                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading ? "Signing in..." : "Sign In"}
                    </Button>

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-card px-2 text-muted-foreground">
                          Or continue with
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        disabled={isLoading}
                        onClick={() => {
                          //toast.info("Google login coming soon!");
                          /* if (!googleIsLoading) {
                            signIn();
                          } */ 
                          handleGoogleLogin();
                        }}
                      >
                        <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                          <path
                            fill="#4285F4"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          />
                          <path
                            fill="#34A853"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          />
                          <path
                            fill="#FBBC05"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          />
                          <path
                            fill="#EA4335"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          />
                        </svg>
                        Google
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        disabled={isLoading}
                        onClick={() => {
                          toast.info("Microsoft login coming soon!");
                        }}
                      >
                        <svg className="mr-2 h-4 w-4" viewBox="0 0 23 23">
                          <path fill="#f3f3f3" d="M0 0h23v23H0z" />
                          <path fill="#f35325" d="M1 1h10v10H1z" />
                          <path fill="#81bc06" d="M12 1h10v10H12z" />
                          <path fill="#05a6f0" d="M1 12h10v10H1z" />
                          <path fill="#ffba08" d="M12 12h10v10H12z" />
                        </svg>
                        Microsoft
                      </Button>
                    </div>

                    {/* <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-card px-2 text-muted-foreground">
                          Demo credentials
                        </span>
                      </div>
                    </div>

                    <div className="rounded-lg bg-muted p-3 space-y-1 text-sm">
                      <p className="text-muted-foreground">
                        Use any email and password to demo the platform
                      </p>
                    </div> */}
                  </form>
                </TabsContent>

                {/* Sign Up Tab */}
                <TabsContent value="signup">
                  {invitation && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Building2 className="h-5 w-5 text-blue-600" />
                        <p className="font-semibold text-blue-900">{invitation.orgName}</p>
                      </div>
                      <p className="text-sm text-blue-700">
                        You've been invited by <strong>{invitation.invitedBy || 'your admin'}</strong> to join this organization.
                        Register with <strong>{invitation.invitedEmail}</strong> to accept the invitation.
                      </p>
                      {invitation.orgDomain && (
                        <p className="text-xs text-blue-500 mt-1">
                          Only @{invitation.orgDomain} email addresses can join this organization.
                        </p>
                      )}
                    </div>
                  )}
                  <form onSubmit={handleSignup} className="space-y-4">
                    {signupError && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{signupError}</AlertDescription>
                      </Alert>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="signup-first-name">First Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signup-first-name"
                          type="text"
                          placeholder="John"
                          value={signupFirstName}
                          onChange={(e) => setSignupFirstName(e.target.value)}
                          className="pl-10"
                          disabled={isLoading}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signup-last-name">Last Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signup-last-name"
                          type="text"
                          placeholder="Doe"
                          value={signupLastName}
                          onChange={(e) => setSignupLastName(e.target.value)}
                          className="pl-10"
                          disabled={isLoading}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signup-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signup-email"
                          type="email"
                          placeholder="john@example.com"
                          value={signupEmail}
                          onChange={(e) => setSignupEmail(e.target.value)}
                          className="pl-10"
                          disabled={isLoading || !!invitation}
                          readOnly={!!invitation}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signup-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signup-password"
                          type={showSignupPassword ? "text" : "password"}
                          placeholder="At least 8 characters including a letter and a number"
                          value={signupPassword}
                          onChange={(e) => setSignupPassword(e.target.value)}
                          className="pl-10 pr-10"
                          disabled={isLoading}
                        />
                        <button
                          type="button"
                          onClick={() => setShowSignupPassword(!showSignupPassword)}
                          className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                        >
                          {showSignupPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signup-confirm-password">Confirm Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signup-confirm-password"
                          type={showSignupPassword ? "text" : "password"}
                          placeholder="Re-enter your password"
                          value={signupConfirmPassword}
                          onChange={(e) => setSignupConfirmPassword(e.target.value)}
                          className="pl-10"
                          disabled={isLoading}
                        />
                      </div>
                    </div>

                    {!invitation && (
                    <div className="space-y-2">
                      <Label>Plan</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          type="button"
                          variant={signupPlan === 'INDIVIDUAL' ? 'default' : 'outline'}
                          className="h-12"
                          onClick={() => { setSignupPlan('INDIVIDUAL'); setOrgExistsForDomain(null); }}
                        >
                          <User className="mr-2 h-4 w-4" />
                          Individual
                        </Button>
                        <Button
                          type="button"
                          variant={signupPlan === 'ENTERPRISE' ? 'default' : 'outline'}
                          className="h-12"
                          onClick={() => { setSignupPlan('ENTERPRISE'); setOrgExistsForDomain(null); }}
                        >
                          <Building2 className="mr-2 h-4 w-4" />
                          Enterprise
                        </Button>
                      </div>
                    </div>
                    )}

                    {!invitation && signupPlan === 'ENTERPRISE' && orgExistsForDomain && (
                      <Alert className="border-amber-200 bg-amber-50">
                        <Info className="h-4 w-4 text-amber-600" />
                        <AlertDescription className="text-amber-800">
                          <strong>{orgExistsForDomain.orgName}</strong> is already registered on WealthBrainAI for your email domain.
                          You can request to join, or ask your admin to send you an invitation link.
                        </AlertDescription>
                      </Alert>
                    )}

                    {!invitation && signupPlan !== 'INDIVIDUAL' && !orgExistsForDomain && (
                      <div className="space-y-2">
                        <Label htmlFor="company-size">Company Size <span className="text-red-500">*</span></Label>
                        <select
                          id="company-size"
                          value={companySize}
                          onChange={(e) => setCompanySize(e.target.value)}
                          required
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        >
                          <option value="">Select company size</option>
                          <option value="1-10">1-10 employees</option>
                          <option value="11-50">11-50 employees</option>
                          <option value="51-200">51-200 employees</option>
                          <option value="201-1000">201-1000 employees</option>
                          <option value="1000+">1000+ employees</option>
                        </select>
                      </div>
                    )}

                    {!invitation && signupPlan === 'ENTERPRISE' && orgCheckLoading && (
                      <div className="text-sm text-muted-foreground flex items-center gap-2">
                        <div className="h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                        Checking if your organization already exists...
                      </div>
                    )}

                    <div className="flex items-start gap-2.5">
                      <Checkbox id="terms" checked={termsAccepted} onCheckedChange={setTermsAccepted} className="mt-0.5 shrink-0" />
                      <Label htmlFor="terms" className="flex cursor-pointer flex-col gap-1.5">
                        <span className="text-sm font-normal text-muted-foreground">I agree to the</span>
                        <span className="flex flex-col gap-1">
                          <button type="button" className="w-fit text-left text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline underline-offset-2 transition-colors" onClick={() => window.open('/terms-of-service.html', '_blank')}>
                            Terms of Service
                          </button>
                          <button type="button" className="w-fit text-left text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline underline-offset-2 transition-colors" onClick={() => window.open('/privacy-policy.html', '_blank')}>
                            Privacy Policy
                          </button>
                          <button type="button" className="w-fit text-left text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline underline-offset-2 transition-colors" onClick={() => window.open('/data-retention-policy.html', '_blank')}>
                            Data Retention Policy
                          </button>
                        </span>
                      </Label>
                    </div>

                    <Button type="submit" className="w-full" disabled={isLoading || !termsAccepted}>
                      {isLoading ? "Creating account..." : orgExistsForDomain ? "Sign Up & Request to Join" : "Create Account"}
                    </Button>
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-card px-2 text-muted-foreground">
                          Or continue with
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        disabled={isLoading}
                        onClick={() => {
                          //toast.info("Google signup coming soon!");
                          handleGoogleLogin();
                        }}
                      >
                        <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                          <path
                            fill="#4285F4"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          />
                          <path
                            fill="#34A853"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          />
                          <path
                            fill="#FBBC05"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          />
                          <path
                            fill="#EA4335"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          />
                        </svg>
                        Google
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        disabled={isLoading}
                        onClick={() => {
                          toast.info("Microsoft signup coming soon!");
                        }}
                      >
                        <svg className="mr-2 h-4 w-4" viewBox="0 0 23 23">
                          <path fill="#f3f3f3" d="M0 0h23v23H0z" />
                          <path fill="#f35325" d="M1 1h10v10H1z" />
                          <path fill="#81bc06" d="M12 1h10v10H12z" />
                          <path fill="#05a6f0" d="M1 12h10v10H1z" />
                          <path fill="#ffba08" d="M12 12h10v10H12z" />
                        </svg>
                        Microsoft
                      </Button>
                    </div>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Forgot Password Dialog */}
          <Dialog open={showForgotPassword} onOpenChange={handleCloseForgotPassword}>
            <DialogContent className="sm:max-w-md">
              {!forgotPasswordSubmitted ? (
                <>
                  <DialogHeader>
                    <DialogTitle>Forgot Password?</DialogTitle>
                    <DialogDescription>
                      Enter your email address and we&apos;ll send you instructions to reset your password
                    </DialogDescription>
                  </DialogHeader>
                  
                  <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
                    {forgotPasswordError && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{forgotPasswordError}</AlertDescription>
                      </Alert>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="forgot-password-email">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="forgot-password-email"
                          type="email"
                          placeholder="name@example.com"
                          value={forgotPasswordEmail}
                          onChange={(e) => setForgotPasswordEmail(e.target.value)}
                          className="pl-10"
                          disabled={forgotPasswordLoading}
                          autoFocus
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Button
                        type="submit"
                        className="w-full"
                        disabled={forgotPasswordLoading}
                      >
                        {forgotPasswordLoading ? (
                          <>
                            <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Mail className="mr-2 h-4 w-4" />
                            Send Reset Instructions
                          </>
                        )}
                      </Button>

                      <Button
                        type="button"
                        variant="ghost"
                        onClick={handleCloseForgotPassword}
                        disabled={forgotPasswordLoading}
                        className="w-full"
                      >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back to Login
                      </Button>
                    </div>
                  </form>
                </>
              ) : (
                <>
                  <DialogHeader>
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="h-8 w-8 text-green-600" />
                    </div>
                    <DialogTitle className="text-center">Check Your Email</DialogTitle>
                    <DialogDescription className="text-center">
                      Password reset instructions have been sent
                    </DialogDescription>
                  </DialogHeader>

                  <Alert>
                    <Mail className="h-4 w-4" />
                    <AlertDescription>
                      We&apos;ve sent password reset instructions to <strong>{forgotPasswordEmail}</strong>.
                      Please check your inbox and follow the link to reset your password.
                    </AlertDescription>
                  </Alert>

                  <div className="space-y-2 text-sm text-muted-foreground">
                    <p>Didn&apos;t receive the email?</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Check your spam or junk folder</li>
                      <li>Make sure you entered the correct email</li>
                      <li>Wait a few minutes for the email to arrive</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <Button
                      onClick={() => setForgotPasswordSubmitted(false)}
                      variant="outline"
                      className="w-full"
                    >
                      Try Different Email
                    </Button>
                    <Button
                      onClick={handleCloseForgotPassword}
                      variant="ghost"
                      className="w-full"
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back to Login
                    </Button>
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>

          <MfaVerificationDialog
            open={showMfaDialog}
            email={mfaType === 'LOGIN' ? loginEmail : signupEmail}
            type={mfaType}
            onVerified={handleMfaVerified}
            onCancel={handleMfaCancel}
          />
        {/* </div> */}
        
        </div>
      </div>
      <Footer />
    </div>
  );
}